import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanSection = ({ 
  section, 
  isExpanded, 
  onToggle, 
  isPremium = false, 
  showUpgradePrompt = false 
}) => {
  const getSectionIcon = (type) => {
    const icons = {
      summary: 'FileText',
      skills: 'Target',
      mindset: 'Brain',
      networking: 'Users',
      weekly_plan: 'Calendar',
      kpis: 'BarChart3'
    };
    return icons?.[type] || 'FileText';
  };

  const getSectionTitle = (type) => {
    const titles = {
      summary: 'Executive Summary',
      skills: 'Skills Development',
      mindset: 'Mindset Coaching',
      networking: 'Networking Strategies',
      weekly_plan: 'Weekly Action Plan',
      kpis: 'Key Performance Indicators'
    };
    return titles?.[type] || 'Plan Section';
  };

  const renderContent = () => {
    // For free tier, allow all AI plan sections; KPIs are premium-only
    const isKpis = String(section?.type).toLowerCase() === 'kpis';
    if (!isPremium && isKpis) {
      return (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
          <p className="text-sm text-warning">KPIs are a premium feature. Upgrade to unlock metrics tracking.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {section?.content && (
          <div className="prose prose-sm max-w-none">
            <p className="text-foreground leading-relaxed whitespace-pre-line">
              {section?.content}
            </p>
          </div>
        )}
        {section?.items && section?.items?.length > 0 && (
          <div className="space-y-3">
            {section?.items?.map((item, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-primary">{index + 1}</span>
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium text-foreground">{item?.title}</h4>
                  <p className="text-sm text-muted-foreground">{item?.description}</p>
                  {item?.timeframe && (
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Clock" size={12} />
                      <span>{item?.timeframe}</span>
                    </div>
                  )}
                  {item?.priority && (
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item?.priority === 'High' ?'bg-error/10 text-error' 
                          : item?.priority === 'Medium' ?'bg-warning/10 text-warning' :'bg-success/10 text-success'
                      }`}>
                        {item?.priority} Priority
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {section?.metrics && section?.metrics?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section?.metrics?.map((metric, index) => (
              <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/20 dark:border-blue-800/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-foreground">{metric?.name}</h4>
                  <Icon name="TrendingUp" size={18} className="text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{metric?.description}</p>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-muted-foreground font-medium">Target:</span>
                  <span className="font-semibold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                    {metric?.target}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-1 flex items-center justify-between hover:bg-muted/50 transition-colors duration-200"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={getSectionIcon(section?.type)} size={20} className="text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground">{getSectionTitle(section?.type)}</h3>
            <p className="text-sm text-muted-foreground">
              {section?.items?.length ? `${section?.items?.length} recommendations` : 'Detailed guidance'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* No premium crown on AI Plan sections; free tier has access */}
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </div>
      </button>
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-border">
          <div className="pt-4">
            {renderContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanSection;